import type {Subkind} from 'shared/constants';
import type {AllowIdentifiedStubs, FieldId, FieldTypeId, Fond, Identified, Rec, RecordEditation, SetFieldValueRequest, TypeOrIdentifiedStub} from 'src/typings';
import type {PromiseBasedRecordEditationDataService} from '../types';
import type {RecordRow} from 'src/features/record-grid/lib/types';
import type {AjaxService} from 'core/data-services/ajax.service';
import {transferify} from 'shared/utils/data-service-utils';

export interface NewRecordEditationParams {
    subkind: Subkind,
    record?: TypeOrIdentifiedStub<Rec | RecordRow>,
    fond?: Fond | Identified<number>,
    rootFond?: Fond | Identified<number>,
    initialValues?: Record<FieldTypeId, SetFieldValueRequest>
}

export class RecordEditationDataService implements PromiseBasedRecordEditationDataService {

    public static readonly serviceName = 'recordEditationDataService';

    private static ROUTE = '/record-editations' as const;
    private static RECORD_EDITATION_V2_ROUTE = '/record-editations/v2' as const;

    /*@ngInject*/
    constructor(private ajaxService: AjaxService) {
    }

    public async createNewEditation(params: AllowIdentifiedStubs<NewRecordEditationParams>): Promise<RecordEditation> {
        if (!params.subkind) {
            throw new Error('subkind param is not defined in new record editation params');
        }

        return this.ajaxService
            .createRequest(RecordEditationDataService.ROUTE)
            .post({...transferify(params), initialValues: params.initialValues});
    }

    public async getEditation(editationId: string): Promise<RecordEditation> {
        return this.ajaxService
            .createRequest(`${RecordEditationDataService.ROUTE}/${editationId}`)
            .get();
    }

    public createField(editationId: string, code: string) {
        return this.doPutTo(editationId, `/fields?code=${code}`);
    }

    public createSubfield(editationId: string, parentId: FieldId, code: string) {
        return this.doPutTo(editationId, `/fields/${encodeURIComponent(parentId)}?code=${encodeURIComponent(code)}`);
    }

    public deleteField(editationId: string, fieldId: FieldId) {
        return this.doDeleteTo(editationId, `/fields/${encodeURIComponent(fieldId)}`);
    }

    public moveField(editationId: string, fieldId: FieldId, isDown: boolean) {
        return this.doPostTo(editationId, `/fields/${encodeURIComponent(fieldId)}/move/${isDown ? 'down' : 'up'}`);
    }

    public setFieldValue(editationId: string, fieldId: FieldId, setFieldValueRequest: SetFieldValueRequest) {
        return this.doPostTo(editationId, `/fields/${encodeURIComponent(fieldId)}`, setFieldValueRequest);
    }

    public clearFieldValue(editationId: string, fieldId: FieldId) {
        return this.doPostTo(editationId, `/fields/${encodeURIComponent(fieldId)}/clear`);
    }

    public setFond(editationId: string, fondId: number) {
        return this.doPostTo(editationId, `/fond/${encodeURIComponent(fondId)}`);
    }

    public save(editationId: string) {
        return this.doPostTo(editationId, '/save');
    }

    public publish(editationId: string) {
        return this.doPostTo(editationId, '/publish');
    }

    public remove(editationId: string) {
        return this.doDeleteTo(editationId, '/delete');
    }

    public setFieldValueByFieldId(editationId: string, fieldId: FieldId, setFieldValueRequest: SetFieldValueRequest): Promise<RecordEditation> {
        return this.ajaxService
            .createRequest(`${RecordEditationDataService.RECORD_EDITATION_V2_ROUTE}/${editationId}/fields/${encodeURIComponent(fieldId)}`)
            .post(setFieldValueRequest);
    }

    public deleteFieldByFieldId(editationId: string, fieldId: FieldId): Promise<RecordEditation> {
        return this.ajaxService
            .createRequest(`${RecordEditationDataService.RECORD_EDITATION_V2_ROUTE}/${editationId}/fields/${encodeURIComponent(fieldId)}`)
            .delete();
    }

    private async doPostTo(editationId: string, pathAndParams: string, data?: any): Promise<RecordEditation> {
        return this.ajaxService
            .createRequest(`${RecordEditationDataService.ROUTE}/${editationId}${pathAndParams}`)
            .post(data);
    }

    private async doPutTo(editationId: string, pathAndParams: string, data?: any): Promise<RecordEditation> {
        return this.ajaxService
            .createRequest(`${RecordEditationDataService.ROUTE}/${editationId}${pathAndParams}`)
            .put(data);
    }

    private async doDeleteTo(editationId: string, pathAndParams: string): Promise<RecordEditation> {
        return this.ajaxService
            .createRequest(`${RecordEditationDataService.ROUTE}/${editationId}${pathAndParams}`)
            .delete();
    }
}
